import {
  Autocomplete,
  CircularProgress,
  ListItem,
  ListItemText,
  TextField,
  useTheme,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import QueryError from "../../../../components/QueryError";
import { FilterModel } from "../../models/FilterModel";
import { AddressPointModel } from "../models/AddressPointModel";
import { useAddressPointsQuery } from "../queries/useAddressPointsQuery";

const isOptionEqualToValue = (
  option: AddressPointModel,
  value: AddressPointModel
) => option.id === value.id;

const AddressPointSelect = () => {
  const theme = useTheme();
  const { watch } = useFormContext<FilterModel>();
  const street = watch("street");
  const queryEnabled = !!street;
  const {
    data: addressPoints,
    error,
    isError,
    refetch,
    isRefetching,
    isLoading,
  } = useAddressPointsQuery(street?.id!, {
    enabled: queryEnabled,
  });
  const options =
    addressPoints?.map((addressPoint) => ({
      ...addressPoint,
      label: `${addressPoint.buildingNumber}${
        addressPoint.name ? ` (${addressPoint.name})` : ""
      }`,
    })) ?? [];

  if (queryEnabled && isError) {
    return (
      <QueryError
        {...{
          error,
          refetch,
          unknownMessage: "Nie udało się pobrać listy budynków",
          ariaUnknownMessage:
            "Nie udało się pobrać listy budynków; spróbuj ponownie, klikając w przycisk spróbuj ponownie",
        }}
      />
    );
  }
  const loading = queryEnabled && (isLoading || (isRefetching && isError));

  return (
    <Controller<FilterModel, "addressPoint">
      name="addressPoint"
      render={({
        field: { onBlur, onChange, value },
        fieldState: { error },
      }) => (
        <Autocomplete
          isOptionEqualToValue={isOptionEqualToValue}
          onBlur={onBlur}
          loading={loading}
          loadingText="Ładowanie..."
          noOptionsText="Wybierz najpierw ulicę"
          options={options}
          renderOption={(props, option) => (
            <ListItem
              {...props}
              secondaryAction={
                <ListItemText
                  primary={option.buildingType}
                  primaryTypographyProps={{
                    fontWeight: "bold",
                    fontSize: theme.typography.pxToRem(14),
                  }}
                />
              }
            >
              <ListItemText
                primary={`${option.buildingNumber}${
                  option.name ? ` (${option.name})` : ""
                }`}
              />
            </ListItem>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={"Numer budynku"}
              required
              error={Boolean(error)}
              helperText={error?.message}
              InputLabelProps={{
                "aria-label": !queryEnabled
                  ? "Numer budynku (wymagane, wybierz najpierw ulicę, aby otrzymać listę budynków)"
                  : "Numer budynku (wymagane)",
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          value={value}
          onChange={(_, newValue) => onChange(newValue)}
        />
      )}
    />
  );
};

export default AddressPointSelect;
